import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useLivePageModeFlag } from '@confluence/live-pages-features/entry-points/useLivePageModeFlag.ts';
import { useLivePageStoreActions } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { usePageContentId } from '@confluence/page-context';

/**
 * Switches a live doc from view to edit mode from the Suggestions panel, announcing it the same way
 * as the live doc mode switcher.
 */
export const useSwitchLiveDocToEditMode = (flags: FlagsStateContainer): (() => void) => {
	const { setMode } = useLivePageStoreActions();
	const { showLivePageModeFlag } = useLivePageModeFlag(flags);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [contentId] = usePageContentId();

	return useCallback(() => {
		showLivePageModeFlag('edit');
		void setMode('edit');
		createAnalyticsEvent?.({
			type: 'sendUIEvent',
			data: {
				source: 'suggestionsPanel',
				action: 'changed',
				actionSubject: 'mode',
				actionSubjectId: 'livePagesModeSwitcher',
				attributes: { previousMode: 'view', newMode: 'edit' },
				objectType: 'page',
				objectId: contentId,
			},
		})?.fire();
	}, [contentId, createAnalyticsEvent, setMode, showLivePageModeFlag]);
};
